import React, { useState, useEffect } from 'react';
import { BsSearch } from 'react-icons/bs';
import { FaCheckCircle, FaCircle } from 'react-icons/fa';
import { MdAdd, MdArrowBackIos, MdClose } from 'react-icons/md';
import { useTaskContext } from '../../../context/TaskContext';
import { useDispatch, useSelector } from 'react-redux';
import sortArrayByKey from '../../../Utils/useSortedArray';
import { useMutation } from '@apollo/client';
import { CREATE_PROJECT, PROJECT_UPDATE } from '../../../apollo/Mutations';
import toast from 'react-hot-toast';
import { set_all_projects } from '../../../redux/taskSlice';
import { IoClose } from "react-icons/io5";
import { LuCheck } from 'react-icons/lu';

const ProjectSelectDetailsPopup = ({ type, onClose, onSelectProject, projectEdit, setEditProject }) => {
    const dispatch = useDispatch();
    const { OnUpdateTask } = useTaskContext();
    const { all_projects, active_task } = useSelector((state) => state.task);
    const [selected_project, setSelected_project] = useState(null);

    const [projectSearch, setProjectSearch] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [newProjectTitle, setNewProjectTitle] = useState('');

    const handleSearchChange = (e) => {
        setSearchValue(e.target.value);
    };

    const [create_project_title] = useMutation(CREATE_PROJECT);
    const [project_update] = useMutation(PROJECT_UPDATE);

    const [editTitle, setEditTitle] = useState(active_task?.project_title || '');
    const updateProjectTitle = async () => {
        if (editTitle.trim()) {
            const normalizedEditTitle = editTitle.trim().toLowerCase();
            if (!all_projects.some((f) => f.project_title.toLowerCase() === normalizedEditTitle)) {
                try {
                    const { data } = await project_update({
                        variables: {
                            input: {
                                project_id: active_task?.project_id,
                                project_title: editTitle.trim(),
                            },
                        },
                    });
                    if (data.project_update.status) {
                        toast.success(data.project_update.message);
                        dispatch(
                            set_all_projects(
                                all_projects.map((project) =>
                                    project._id === active_task?.project_id
                                        ? { ...project, project_title: editTitle.trim() }
                                        : project
                                )
                            )
                        );
                        OnUpdateTask({
                            project_id: active_task?.project_id,
                            project_title: editTitle.trim(),
                            save_type: 'project_title',
                        });
                        setEditProject(false)
                    }
                } catch (error) {
                    console.error(error);
                    toast.error('Failed to update the project title. Please try again.');
                }
            } else {
                toast.error(`${editTitle} already exists.`);
            }
        }
    }

    const handleAddTitle = async () => {
        try {
            const normalizedTitle = newProjectTitle.trim().toLowerCase();
            if (!all_projects.some((f) => f.project_title.toLowerCase() === normalizedTitle)) {
                const { data } = await create_project_title({
                    variables: { input: { project_title: newProjectTitle } },
                });
                toast.success(data.create_project.message);
                let d = {
                    _id: data.create_project.data[0]._id,
                    project_title: data.create_project.data[0].project_title,
                }
                dispatch(set_all_projects([d, ...all_projects]));
                setNewProjectTitle('');
                handleSelectTitle(d);
            } else {
                toast.error(`${newProjectTitle} already exists.`);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to add the project. Please try again.');
        }
    };

    const handleSelectTitle = (project) => {
        setSelected_project(project);
        if (type === 'create') {
            onSelectProject(project);
            onClose();
        } else {

            OnUpdateTask({
                project_id: project._id,
                project_title: project.project_title,
                save_type: 'project_title',
            });
        }
    };


    useEffect(() => {
        if (all_projects?.length) {
            setSelected_project(all_projects.find((f) => f._id === active_task?.project_id) || null);
        }
    }, [all_projects, active_task]);

    // Filtered and sorted projects
    const filteredProjects = all_projects?.filter(
        (project) =>
            project._id !== 'all' &&
            project.project_title.toLowerCase().includes(newProjectTitle.trim().toLowerCase() || searchValue.trim().toLowerCase())
    );
    const sorted_data = sortArrayByKey(filteredProjects, 'project_title', 'asc');

    return (
        <div className="relative task directMsgPopup animate__animated animate__zoomIn animation_duration bgSurfaceDark z-[22]">
            <header className="filterFileHeader">
                <div className="allfilterFileHeader !ml-2" onClick={onClose}>
                    <MdArrowBackIos className="allFileHeaderIcon text-2xl !mt-[0px]" />
                    <span className="allFileHeaderText">Task project</span>
                </div>
                <div className='flex gap-2 items-center relative mr-2'>
                    <BsSearch
                        onClick={() => setProjectSearch(!projectSearch)}
                        className='text-white text-lg mx-2 cursor-pointer'
                    />
                </div>
            </header>
            {projectSearch && (
                <div className="w-full absolute px-3 top-0 bg-teal-600 animate__animated animate__fadeIn animate__faster">
                    <div className="flex items-center h-[52px]">
                        <div onClick={() => setProjectSearch(false)} className="cursor-pointer">
                            <MdArrowBackIos className='text-2xl mr-2 text-white' />
                        </div>
                        <div className='w-full relative'>
                            <input
                                className='pl-2 rounded w-full h-10 text-text-body'
                                placeholder="Search project"
                                type="text"
                                onChange={handleSearchChange}
                                value={searchValue}
                                autoFocus
                            />
                            {searchValue && (
                                <IoClose
                                    onClick={() => setSearchValue('')}
                                    className="absolute text-text-body right-3 top-1/2 transform -translate-y-1/2 text-xl cursor-pointer"
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
            <div className="px-4 py-3 flex items-center projectTitleInput relative h-[58px]">
                <input
                    type="text"
                    className="w-full border border-teal-200 h-[32px] rounded-sm pl-2 px-1 pr-2 text-sm"
                    id="taskProjectTitle"
                    placeholder="Add a new project title or pick from below"
                    maxLength="64"
                    value={newProjectTitle}
                    onChange={(e) => setNewProjectTitle(e.target.value)}
                />
                {newProjectTitle && (
                    <MdClose className={`text-gray-400 text-xl absolute right-14`} onClick={() => setNewProjectTitle('')} />
                )}
                <MdAdd className={`text-teal-600 text-xl absolute right-6 ${!newProjectTitle ? 'pointer-events-none opacity-[0.6]' : ''}`} onClick={handleAddTitle} />
            </div>
            <div className='!h-[calc(100vh-130px)] w-full overflow-auto'>
                {sorted_data.filter(f => f._id !== 'all' && f.project_title?.trim() !== '').map((project, index) => (
                    <div key={project._id} className={`flex items-center border-b border-b-teal-200 h-[45px] ${selected_project?._id === project._id ? 'bg-[#ECEEF4]' : 'hover:bg-[#ECEEF4]'}`}>
                        <p className="w-[10%] flex justify-start items-center mr-2">
                            {selected_project?._id === project._id ? (
                                <FaCheckCircle className="text-lg ml-5 text-teal-600 " />
                            ) : (
                                <FaCircle className="text-lg ml-5 text-teal-200" />
                            )}
                        </p>
                        {projectEdit && selected_project?._id === project?._id ? (
                            <div className='flex gap-2 items-center w-[90%]'>
                                <input
                                    className='border-b w-[80%] rounded pl-1 pr-1 py-0.5 break-all border border-teal-600 text-teal-600  text-sm outline-none min-w-[80px]'
                                    value={editTitle}
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    autoFocus
                                />
                                <div className='flex gap-1'>
                                    <IoClose className="text-lg text-text-body cursor-pointer" onClick={() => { setEditTitle(''); setEditProject(false) }} />
                                    <LuCheck className="text-lg text-text-body cursor-pointer" onClick={() => updateProjectTitle()} />
                                </div>
                            </div>
                        ) : (
                            <p className="titleProject w-[80%] py-[10px] whitespace-nowrap overflow-ellipsis text-teal-600 overflow-hidden flex justify-between items-center px-4 !pl-0 text-sm truncate cursor-pointer"
                                onClick={() => handleSelectTitle(project)}
                            >
                                {project.project_title}
                            </p>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProjectSelectDetailsPopup;